import React, { useState } from 'react';
import { X, Upload, FileText, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AILeadModal = ({ isOpen, onClose, onLeadCreated }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null); // Store file for later attachment
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setTextContent(''); // Clear text if file is selected
      setError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a PDF, Word document, or text file');
        return;
      }
      setSelectedFile(file);
      setTextContent(''); // Clear text if file is selected
      setError('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText) {
      setTextContent(pastedText);
      setSelectedFile(null); // Clear file if text is pasted
      setError('');
    }
  };

  const handleGenerate = async () => {
    if (!selectedFile && !textContent.trim()) {
      setError('Please upload a file or paste text content');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      } else {
        formData.append('text_content', textContent);
      }

      const response = await axios.post(`${API}/ai/generate-lead`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Pass extracted data AND the uploaded file to parent
        if (onLeadCreated) {
          onLeadCreated({
            extractedData: response.data.extracted_data,
            uploadedFile: selectedFile // Pass the file object
          });
        }
        // Reset and close
        handleClose();
      }
    } catch (err) {
      console.error('Failed to generate lead:', err);
      setError(err.response?.data?.detail || 'Failed to generate lead. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTextContent('');
    setSelectedFile(null);
    setUploadedFile(null);
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-blue-600" />
            AI Lead Generation
          </DialogTitle>
          <DialogDescription>
            Upload a tender document or paste text content. AI will analyze it and create a new project lead with pre-filled information.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {selectedFile ? (
              <div className="space-y-2">
                <FileText className="w-12 h-12 mx-auto text-blue-600" />
                <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                  className="mt-2"
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="w-12 h-12 mx-auto text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Drag and drop your file here
                  </p>
                  <p className="text-xs text-gray-500">or</p>
                </div>
                <div>
                  <input
                    type="file"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label htmlFor="file-upload">
                    <Button variant="outline" size="sm" asChild>
                      <span>Browse Files</span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  Supported: PDF, Word, Text files
                </p>
              </div>
            )}
          </div>

          {/* OR Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or paste text</span>
            </div>
          </div>

          {/* Text Input Area */}
          <div>
            <textarea
              value={textContent}
              onChange={(e) => {
                setTextContent(e.target.value);
                setSelectedFile(null); // Clear file if typing
              }}
              onPaste={handlePaste}
              placeholder="Paste tender document text here..."
              className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              You can paste text directly or press Ctrl+V
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={loading || (!selectedFile && !textContent.trim())}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate Lead
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AILeadModal;
